import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRound, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

import Logo, { BRAND_BLUE } from './Logo';
import LoginIllustration from './LoginIllustration';

// "Remember my user ID" — only the ID, never the PIN, and only in this browser.
// Wrapped because storage can be blocked (private windows, strict settings);
// the page works the same without it.
const REMEMBER_KEY = 'servpatch:rememberedUserId';
const readRemembered = () => {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
};
const writeRemembered = (value) => {
  try {
    if (value) localStorage.setItem(REMEMBER_KEY, value);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch { /* storage unavailable — nothing to remember */ }
};

/**
 * An outlined field whose label sits on its top border.
 * Kept here: the sign-in page is the only place that uses this look.
 */
const Field = ({ id, label, icon: Icon, children, trailing }) => (
  <div className="relative">
    <label
      htmlFor={id}
      className="absolute -top-2.5 left-3 px-1.5 text-[12px] font-semibold text-slate-500 dark:text-gray-400 bg-white dark:bg-[#262b33] z-10"
    >
      {label} <span className="text-rose-500">*</span>
    </label>
    <div className="flex items-center h-[52px] rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-[#262b33] focus-within:border-[#0B3F8F] focus-within:ring-4 focus-within:ring-[#0B3F8F]/10 dark:focus-within:border-blue-400 dark:focus-within:ring-blue-400/10 transition-all">
      <Icon className="ml-4 w-[18px] h-[18px] text-slate-400 dark:text-gray-500 shrink-0" />
      {children}
      {trailing}
    </div>
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({ user_id: readRemembered(), pin: '' }));
  const [remember, setRemember] = useState(() => Boolean(readRemembered()));
  const [showPassword, setShowPassword] = useState(false);
  const pinRef = useRef(null);

  // With the ID already remembered, the PIN is the only thing left to type.
  useEffect(() => {
    if (formData.user_id) pinRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    // Stops the browser from refreshing the page.
    e.preventDefault();

    if (!formData.user_id.trim()) {
      toast.error('User ID is required');
      return;
    }
    if (!formData.pin.trim()) {
      toast.error('PIN is required');
      return;
    }

    setIsLoading(true);
    try {
      const response = await login(formData.user_id.trim(), formData.pin);

      if (response.success) {
        writeRemembered(remember ? formData.user_id.trim() : '');
        toast.success('Login successful!');
        navigate('/');
      } else {
        toast.error(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      toast.error('An error occurred connecting to the server. Please try again.');
      console.error('Login API Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F4F7FC] dark:bg-[#1b1f26]">
      <style>{`
        @keyframes sp-login-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes sp-login-hand { 0%, 55%, 100% { transform: rotate(0); } 65% { transform: rotate(18deg); } 75% { transform: rotate(-8deg); } 85% { transform: rotate(14deg); } }
        .sp-login-rise { animation: sp-login-rise .7s cubic-bezier(.2,.7,.2,1) both; }
        .sp-login-rise-2 { animation: sp-login-rise .7s .12s cubic-bezier(.2,.7,.2,1) both; }
        .sp-login-rise-3 { animation: sp-login-rise .7s .24s cubic-bezier(.2,.7,.2,1) both; }
        .sp-login-hand { display: inline-block; transform-origin: 70% 80%; animation: sp-login-hand 2.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .sp-login-rise, .sp-login-rise-2, .sp-login-rise-3, .sp-login-hand { animation: none !important; }
        }
      `}</style>

      {/* ── Left: welcome + illustration (wide screens) ── */}
      <aside className="hidden lg:flex lg:w-[48%] xl:w-[46%] relative flex-col justify-between bg-white dark:bg-[#20242c] border-r border-slate-100 dark:border-gray-800 px-12 xl:px-16 py-10 overflow-hidden">
        {/* soft corner glows */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 rounded-full bg-blue-100/60 dark:bg-blue-900/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-20 w-96 h-96 rounded-full bg-teal-100/50 dark:bg-teal-900/10 blur-3xl" />

        <Logo size="md" className="relative sp-login-rise" />

        <div className="relative">
          <h1 className="sp-login-rise text-[40px] xl:text-[44px] leading-[1.1] font-extrabold tracking-tight text-slate-900 dark:text-gray-100">
            Hi, welcome back <span className="sp-login-hand" aria-hidden="true">👋</span>
          </h1>
          <p className="sp-login-rise-2 mt-3 text-[16px] text-slate-500 dark:text-gray-400 max-w-md">
            Sign in to look after your residents, rotas and team — all in one place.
          </p>
          <LoginIllustration className="sp-login-rise-3 mt-8 max-w-[520px]" />
        </div>

        <p className="relative text-[12px] text-slate-400 dark:text-gray-500">
          © {new Date().getFullYear()} ServPatch · Care management, made simple
        </p>
      </aside>

      {/* ── Right: the sign-in card ── */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-[440px] sp-login-rise-2">
          <div className="bg-white dark:bg-[#262b33] rounded-[24px] border border-slate-100 dark:border-gray-800 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] px-7 py-9 sm:px-10 sm:py-11">
            <div className="flex flex-col items-center text-center">
              <Logo size="md" />
              <h2 className="mt-6 text-[22px] font-bold text-slate-900 dark:text-gray-100 tracking-tight">
                Sign in to your account
              </h2>
              <p className="mt-1.5 text-[14px] text-slate-500 dark:text-gray-400">
                Use the user ID from your welcome email and your PIN.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
              <Field id="user_id" label="User ID" icon={UserRound}>
                <input
                  type="text"
                  id="user_id"
                  name="user_id"
                  value={formData.user_id}
                  onChange={handleChange}
                  placeholder="e.g. CH742816"
                  className="flex-1 min-w-0 h-full bg-transparent px-3 text-[15px] text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none"
                  disabled={isLoading}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Field>

              <Field
                id="pin"
                label="PIN"
                icon={Lock}
                trailing={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide PIN' : 'Show PIN'}
                    className="mr-2 p-2 rounded-lg text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-50 dark:hover:bg-[#31373f] transition"
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                  </button>
                )}
              >
                <input
                  ref={pinRef}
                  type={showPassword ? 'text' : 'password'}
                  id="pin"
                  name="pin"
                  value={formData.pin}
                  onChange={(e) => handleChange({
                    target: { name: 'pin', value: e.target.value.replace(/\D/g, '').slice(0, 6) },
                  })}
                  placeholder="Your 6-digit PIN"
                  inputMode="numeric"
                  maxLength={6}
                  className="flex-1 min-w-0 h-full bg-transparent px-3 text-[15px] tracking-[0.2em] placeholder:tracking-normal text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </Field>

              <div className="flex items-center justify-between gap-3 -mt-1">
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded accent-[#0B3F8F] cursor-pointer"
                  />
                  <span className="text-[13px] text-slate-600 dark:text-gray-300">Remember my user ID</span>
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-pin')}
                  className="text-[13px] font-semibold text-[#0B3F8F] dark:text-blue-400 hover:underline underline-offset-4"
                >
                  Forgot PIN?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{ backgroundColor: BRAND_BLUE }}
                className="w-full h-[52px] rounded-xl text-white text-[15px] font-semibold flex items-center justify-center gap-2 shadow-[0_12px_24px_-10px_rgba(11,63,143,0.7)] hover:brightness-110 active:brightness-95 transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-[18px] w-[18px]" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            <div className="mt-8 flex items-center justify-center gap-2 text-[12px] text-slate-400 dark:text-gray-500">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              Secure sign-in · Never share your PIN with anyone
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
